import { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { useAuthMethod } from '../hooks/useAuthMethod';
import api from '../services/api';
import { User, Agency, Group } from '../types';

function Users() {
  const { user: currentUser } = useUser();
  const { localEnabled } = useAuthMethod();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    isActive: true,
    agencyId: '',
    groupId: '',
    roleId: '',
    password: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState({ 
    firstName: '', 
    lastName: '', 
    email: '', 
    agencyId: '',
    groupId: '',
    roleId: '',
    password: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [roles, setRoles] = useState<Array<{id: string, name: string}>>([]);

  useEffect(() => {
    fetchUsers();
    fetchAgencies();
    fetchGroups();
    fetchRoles();
  }, [currentUser]);

  const fetchUsers = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      let response;
      
      // Check if user has admin role (can see all users)
      const isAdmin = currentUser.roles.some(role => role.id === 'admin-role-id');
      const isGroupAdmin = currentUser.roles.some(role => role.id === 'group-admin-role-id');
      const isCustomerAdmin = currentUser.roles.some(role => role.id === 'customer-admin-role-id');
      
      if (isAdmin) {
        // Admin can see all users
        response = await api.get('/users');
      } else {
        // Agency admin can only see users from their agency
        const isAgencyAdmin = currentUser.roles.some(role => role.id === 'agency-admin-role-id');
        if (isAgencyAdmin) {
          response = await api.get(`/users?agencyId=${currentUser.agency.id}`);
        } else if (isGroupAdmin || isCustomerAdmin) {
          response = await api.get(`/users?groupId=${(currentUser as any).groupId || ''}`);
        } else {
          // Other users shouldn't access this page, but handle gracefully
          setError('You do not have permission to view users.');
          setLoading(false);
          return;
        }
      }

      setUsers(response.data);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      const d = err.response?.data;
      setError(d?.message || d?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await api.get('/agencies');
      setAgencies(response.data);
    } catch (err: any) {
      console.error('Failed to fetch agencies:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      // This would typically come from a roles endpoint
      // For now, we'll use hardcoded roles that match the database seed values
      setRoles([
        { id: 'customer-role-id', name: 'Customer' },
        { id: 'customer-admin-role-id', name: 'Customer Admin' },
        { id: 'group-user-role-id', name: 'Group User' },
        { id: 'group-admin-role-id', name: 'Group Admin' },
        { id: 'agency-user-role-id', name: 'Agency User' },
        { id: 'agency-admin-role-id', name: 'Agency Admin' },
        { id: 'admin-role-id', name: 'System Admin' }
      ]);
    } catch (err: any) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch groups:', err);
      setGroups([]);
    }
  };

  const canManageUsers = currentUser?.roles.some(
    (role) =>
      role.id === 'admin-role-id' ||
      role.id === 'agency-admin-role-id' ||
      role.id === 'group-admin-role-id' ||
      role.id === 'customer-admin-role-id',
  );

  /** Group-scoped user managers: group dropdown locked to their group (same as backend scope). */
  const isGroupLockedUserManager = !!currentUser?.roles.some(
    (r) => r.id === 'group-admin-role-id' || r.id === 'customer-admin-role-id',
  );

  /** Customer admins are fixed to their group; hide redundant picker (group admins still see it). */
  const hideUsersGroupPicker =
    !!currentUser?.roles.some((r) => r.id === 'customer-admin-role-id') &&
    !currentUser?.roles.some((r) => r.id === 'group-admin-role-id');

  const getGroupDisplay = (user: User) => {
    if (!user.groupId) return 'No group';
    const group = groups.find((g) => g.id === user.groupId);
    return group ? group.name : user.groupId;
  };

  const createAgencyGroups = groups.filter(
    (g) => g.agencyId === createFormData.agencyId && g.isActive,
  );

  const editAgencyGroups = groups.filter(
    (g) =>
      g.agencyId === editFormData.agencyId &&
      (g.isActive || g.id === editFormData.groupId),
  );

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    // Get the first role ID from userRoles (assuming single role for now)
    const userRoleId = (user as any).userRoles?.[0]?.role?.id || '';
    setEditFormData({ 
      firstName: user.firstName, 
      lastName: user.lastName, 
      email: user.email,
      isActive: user.isActive,
      agencyId: user.agency?.id || '',
      groupId: (user.groupId as string) || '',
      roleId: userRoleId,
      password: ''
    });
    setIsEditModalOpen(true);
    setUpdateError(null);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setEditFormData({ firstName: '', lastName: '', email: '', isActive: true, agencyId: '', groupId: '', roleId: '', password: '' });
    setUpdateError(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'agencyId' ? { groupId: '' } : {}),
    }));
  };

  const handleDeleteClick = (user: User) => {
    setDeletingUser(user);
    setIsDeleteModalOpen(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingUser(null);
    setDeleteError(null);
  };

  const handleCreateClick = () => {
    // Check if current user is agency admin or group admin
    const isAgencyAdmin = currentUser?.roles.some(role => role.id === 'agency-admin-role-id');
    const isGroupAdmin = currentUser?.roles.some(role => role.id === 'group-admin-role-id');
    const isCustomerAdmin = currentUser?.roles.some(role => role.id === 'customer-admin-role-id');
    
    // Preselect agency and role based on user permissions
    let initialFormData = { firstName: '', lastName: '', email: '', agencyId: '', groupId: '', roleId: '', password: '' };
    
    if (isAgencyAdmin) {
      // Agency admin can only create users in their own agency
      initialFormData.agencyId = currentUser?.agency.id || '';
      // Preselect "AgencyUser" role (assuming this is the agency-user-role-id)
      initialFormData.roleId = 'agency-user-role-id';
    } else if (isGroupAdmin) {
      // Group admin can only create users in their own agency/group
      initialFormData.agencyId = currentUser?.agency.id || '';
      initialFormData.groupId = (currentUser as any)?.groupId || '';
      initialFormData.roleId = 'group-user-role-id';
    } else if (isCustomerAdmin) {
      initialFormData.agencyId = currentUser?.agency.id || '';
      initialFormData.groupId = (currentUser as any)?.groupId || '';
      initialFormData.roleId = 'customer-role-id';
    }
    
    setCreateFormData(initialFormData);
    setIsCreateModalOpen(true);
    setCreateError(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateFormData({ firstName: '', lastName: '', email: '', agencyId: '', groupId: '', roleId: '', password: '' });
    setCreateError(null);
  };

  const handleCreateFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'agencyId' ? { groupId: '' } : {}),
    }));
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await api.delete(`/users/${deletingUser.id}`);

      // Remove the user from the list
      setUsers(prev => prev.filter(user => user.id !== deletingUser.id));

      handleCloseDeleteModal();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      
      // Handle specific business logic errors
      const d = err.response?.data;
      const errorMessage = d?.message || d?.error || err.message || 'Failed to delete user';
      
      if (errorMessage.includes('existing transactions')) {
        setDeleteError('Cannot delete this user because they have associated transactions. Please remove all transactions from this user first.');
      } else if (errorMessage.includes('existing uploads')) {
        setDeleteError('Cannot delete this user because they have associated uploads. Please remove all uploads from this user first.');
      } else {
        setDeleteError(errorMessage);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateUser = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const effectiveAgencyId = isGroupLockedUserManager
        ? (currentUser?.agency.id || createFormData.agencyId)
        : createFormData.agencyId;
      const effectiveGroupId = isGroupLockedUserManager
        ? ((currentUser as any)?.groupId || createFormData.groupId)
        : createFormData.groupId;

      const payload: Record<string, unknown> = {
        firstName: createFormData.firstName,
        lastName: createFormData.lastName,
        email: createFormData.email,
        agencyId: effectiveAgencyId,
        groupId: effectiveGroupId || null,
        roleId: createFormData.roleId,
        isActive: true,
      };
      if (localEnabled && createFormData.password.trim().length > 0) {
        payload.password = createFormData.password;
      }

      const response = await api.post('/users', payload);

      // Add the new user to the list
      setUsers(prev => [response.data, ...prev]);

      handleCloseCreateModal();
    } catch (err: any) {
      console.error('Failed to create user:', err);
      const dc = err.response?.data;
      setCreateError(dc?.message || dc?.error || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const effectiveAgencyId = isGroupLockedUserManager
        ? (currentUser?.agency.id || editFormData.agencyId)
        : editFormData.agencyId;
      const effectiveGroupId = isGroupLockedUserManager
        ? ((currentUser as any)?.groupId || editFormData.groupId)
        : editFormData.groupId;

      const updatePayload: Record<string, unknown> = {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        email: editFormData.email,
        isActive: editFormData.isActive,
        agencyId: effectiveAgencyId,
        groupId: effectiveGroupId || null,
        roleId: editFormData.roleId
      };

      // Include password if provided (sets local auth password for existing user)
      if (editFormData.password && editFormData.password.trim().length > 0) {
        updatePayload.password = editFormData.password;
      }

      console.log('UpdateUser - Sending data:', { ...updatePayload, password: updatePayload.password ? '[REDACTED]' : undefined });
      
      const response = await api.put(`/users/${editingUser.id}`, updatePayload);
      
      console.log('UpdateUser - Response data:', response.data);

      // Update the users list with the updated user
      setUsers(prev => prev.map(user => 
        user.id === editingUser.id 
          ? { ...user, ...response.data, updatedAt: new Date().toISOString() }
          : user
      ));

      handleCloseModal();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      const d = err.response?.data;
      setUpdateError(d?.message || d?.error || 'Failed to update user');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1>User Management</h1>
        <div style={{ marginTop: '20px', color: 'var(--secondary-color)' }}>
          Loading users...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>User Management</h1>
        <div style={{ marginTop: '20px', color: 'var(--primary-color)' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1>User Management</h1>
      
      {canManageUsers && (
        <div style={{ marginTop: '20px' }}>
          <button 
            onClick={handleCreateClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'none',
              border: '1px solid #FC5003',
              color: '#FC5003',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '1rem',
              fontWeight: '600'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span className="material-symbols-outlined small">add</span>
            Add New User
          </button>
        </div>
      )}

      <div style={{ marginTop: '30px' }}>
        <h2>Users List</h2>
        
        {users.length === 0 ? (
          <p style={{ color: 'var(--secondary-color)' }}>No users found.</p>
        ) : (
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ 
                  backgroundColor: 'var(--background-color)', 
                  borderBottom: '1px solid #e0e0e0' 
                }}>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Name
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Email
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Agency
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Group
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Roles
                  </th>
                  <th style={{ 
                    padding: '12px 16px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    color: 'var(--secondary-color)'
                  }}>
                    Status
                  </th>
                  {canManageUsers && (
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: 'var(--secondary-color)'
                    }}>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ 
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <td style={{ padding: '12px 16px' }}>
                      {user.firstName} {user.lastName}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {user.email}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {user.agency?.name || 'Unknown'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {getGroupDisplay(user)}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {(user as any).userRoles?.map((ur: any) => ur.role.name).join(', ') || 'No roles'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: user.isActive ? '#d4edda' : '#f8d7da',
                        color: user.isActive ? '#155724' : '#721c24'
                      }}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManageUsers && (
                      <td style={{ padding: '12px 16px' }}>
                        <button 
                          onClick={() => handleEditClick(user)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            marginRight: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Edit user"
                          aria-label="Edit user"
                        >
                          <span className="material-symbols-outlined primary">edit</span>
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(user)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Delete user"
                          aria-label="Delete user"
                        >
                          <span className="material-symbols-outlined error">delete</span>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {isEditModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              color: 'var(--primary-color)',
              fontSize: '20px'
            }}>
              Edit User
            </h2>
            
            {updateError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {updateError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={editFormData.firstName}
                onChange={handleFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter first name"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={editFormData.lastName}
                onChange={handleFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter last name"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Email
              </label>
              <input
                type="email"
                name="email"
                value={editFormData.email}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'not-allowed'
                }}
                placeholder="Email address (read-only)"
              />
            </div>

            {/* Only show agency dropdown for system admins (admin-role-id) */}
            {currentUser?.roles.some(role => role.id === 'admin-role-id') && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Agency *
                </label>
                <select
                  name="agencyId"
                  value={editFormData.agencyId}
                  onChange={handleFormChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select an agency</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Agency must be hidden for users without admin-role-id */}

            {canManageUsers && !hideUsersGroupPicker && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Group
                </label>
                <select
                  name="groupId"
                  value={editFormData.groupId}
                  onChange={handleFormChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  disabled={!editFormData.agencyId || isGroupLockedUserManager}
                >
                  <option value="">No group</option>
                  {editAgencyGroups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  {isGroupLockedUserManager
                    ? 'You can only manage users in your assigned group.'
                    : 'Only groups from the selected agency are available.'}
                </div>
              </div>
            )}

            {/* Show role dropdown for admins and agency admins; restrict admin option unless current user is admin */}
            {canManageUsers && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Role
                </label>
                <select
                  name="roleId"
                  value={editFormData.roleId}
                  onChange={handleFormChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a role (optional)</option>
                  {roles
                    .filter(role => {
                      const isCurrentUserAdmin = currentUser?.roles.some(r => r.id === 'admin-role-id');
                      const isCurrentUserGroupAdmin = currentUser?.roles.some(r => r.id === 'group-admin-role-id');
                      const isCurrentUserCustomerAdmin = currentUser?.roles.some(r => r.id === 'customer-admin-role-id');
                      if (!isCurrentUserAdmin && role.id === 'admin-role-id') {
                        return false;
                      }
                      if (isCurrentUserGroupAdmin && !['group-user-role-id', 'group-admin-role-id'].includes(role.id)) {
                        return false;
                      }
                      if (
                        isCurrentUserCustomerAdmin &&
                        !isCurrentUserGroupAdmin &&
                        !['customer-role-id', 'customer-admin-role-id'].includes(role.id)
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Hide role info when dropdown is available; show only for non-privileged users */}
            {!canManageUsers && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Role
                </label>
                <div style={{
                  padding: '10px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#6c757d'
                }}>
                  {(editingUser as any)?.userRoles?.[0]?.role?.name || 'No role assigned'} (Current Role)
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#6c757d',
                  marginTop: '4px'
                }}>
                  Agency admins cannot change user roles
                </div>
              </div>
            )}

            {/* Password field for local auth - only show when local auth is enabled */}
            {localEnabled && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  New Password (optional)
                </label>
                <input
                  type="password"
                  name="password"
                  value={editFormData.password}
                  onChange={handleFormChange}
                  autoComplete="new-password"
                  placeholder="Leave empty to keep current password"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                  Set a password to enable local (email/password) sign-in for this user.
                </p>
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={editFormData.isActive}
                  onChange={handleFormChange}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
                Active User
              </label>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCloseModal}
                disabled={isUpdating}
                className="btn-outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={isUpdating || !editFormData.firstName.trim() || !editFormData.lastName.trim() || !editFormData.email.trim() || (currentUser?.roles.some(role => role.id === 'admin-role-id') && !editFormData.agencyId)}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isUpdating || !editFormData.firstName.trim() || !editFormData.lastName.trim() || !editFormData.email.trim() || (currentUser?.roles.some(role => role.id === 'admin-role-id') && !editFormData.agencyId) ? 0.6 : 1,
                  cursor: isUpdating || !editFormData.firstName.trim() || !editFormData.lastName.trim() || !editFormData.email.trim() || (currentUser?.roles.some(role => role.id === 'admin-role-id') && !editFormData.agencyId) ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="material-symbols-outlined small">save</span>
                {isUpdating ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              color: '#dc3545',
              fontSize: '20px'
            }}>
              Confirm Delete
            </h2>
            
            {deleteError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {deleteError}
              </div>
            )}

            <p style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              color: '#333'
            }}>
              Are you sure you want to delete the user <strong>"{deletingUser.firstName} {deletingUser.lastName}"</strong>? 
              This action cannot be undone.
            </p>

            <div style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '8px', fontWeight: '600', color: '#495057' }}>
                User Information:
              </div>
              <div style={{ color: '#6c757d' }}>
                • Name: {deletingUser.firstName} {deletingUser.lastName}
              </div>
              <div style={{ color: '#6c757d' }}>
                • Email: {deletingUser.email}
              </div>
              <div style={{ color: '#6c757d' }}>
                • Agency: {deletingUser.agency?.name || 'Unknown'}
              </div>
              <div style={{ color: '#6c757d' }}>
                • Status: {deletingUser.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="btn-outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: isDeleting ? '#ccc' : '#dc3545',
                  opacity: isDeleting ? 0.6 : 1,
                  cursor: isDeleting ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="material-symbols-outlined small">delete</span>
                {isDeleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{
              margin: '0 0 20px 0',
              color: 'var(--primary-color)',
              fontSize: '20px'
            }}>
              Create New User
            </h2>
            
            {createError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {createError}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={createFormData.firstName}
                onChange={handleCreateFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter first name"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={createFormData.lastName}
                onChange={handleCreateFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter last name"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: 'var(--secondary-color)'
              }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={createFormData.email}
                onChange={handleCreateFormChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
                placeholder="Enter email address"
              />
            </div>

            {localEnabled && (
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '500',
                    color: 'var(--secondary-color)',
                  }}
                >
                  Initial password (optional)
                </label>
                <input
                  type="password"
                  name="password"
                  value={createFormData.password}
                  onChange={handleCreateFormChange}
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                  placeholder="Min 8 characters — requires admin token; skips Azure invite"
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                  If set, the user signs in with email/password. Azure B2B invite is skipped.
                </p>
              </div>
            )}

            {/* Only show agency dropdown for system admins (admin-role-id) */}
            {currentUser?.roles.some(role => role.id === 'admin-role-id') && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Agency *
                </label>
                <select
                  name="agencyId"
                  value={createFormData.agencyId}
                  onChange={handleCreateFormChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select an agency</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.id}>
                      {agency.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Agency must be hidden for users without admin-role-id */}

            {canManageUsers && !hideUsersGroupPicker && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Group
                </label>
                <select
                  name="groupId"
                  value={createFormData.groupId}
                  onChange={handleCreateFormChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                  disabled={!createFormData.agencyId || isGroupLockedUserManager}
                >
                  <option value="">No group</option>
                  {createAgencyGroups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  {isGroupLockedUserManager
                    ? 'You can only manage users in your assigned group.'
                    : 'Only groups from the selected agency are available.'}
                </div>
              </div>
            )}

            {/* Show role dropdown for admins and agency admins; restrict admin option unless current user is admin */}
            {canManageUsers && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: 'var(--secondary-color)'
                }}>
                  Role
                </label>
                <select
                  name="roleId"
                  value={createFormData.roleId}
                  onChange={handleCreateFormChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a role (optional)</option>
                  {roles
                    .filter(role => {
                      const isCurrentUserAdmin = currentUser?.roles.some(r => r.id === 'admin-role-id');
                      const isCurrentUserGroupAdmin = currentUser?.roles.some(r => r.id === 'group-admin-role-id');
                      const isCurrentUserCustomerAdmin = currentUser?.roles.some(r => r.id === 'customer-admin-role-id');
                      if (!isCurrentUserAdmin && role.id === 'admin-role-id') {
                        return false;
                      }
                      if (isCurrentUserGroupAdmin && !['group-user-role-id', 'group-admin-role-id'].includes(role.id)) {
                        return false;
                      }
                      if (
                        isCurrentUserCustomerAdmin &&
                        !isCurrentUserGroupAdmin &&
                        !['customer-role-id', 'customer-admin-role-id'].includes(role.id)
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCloseCreateModal}
                disabled={isCreating}
                className="btn-outline"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span className="material-symbols-outlined small">close</span>
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isCreating || !createFormData.firstName.trim() || !createFormData.lastName.trim() || !createFormData.email.trim() || (currentUser?.roles.some(role => role.id === 'admin-role-id') && !createFormData.agencyId)}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: isCreating || !createFormData.firstName.trim() || !createFormData.lastName.trim() || !createFormData.email.trim() || (currentUser?.roles.some(role => role.id === 'admin-role-id') && !createFormData.agencyId) ? 0.6 : 1,
                  cursor: isCreating || !createFormData.firstName.trim() || !createFormData.lastName.trim() || !createFormData.email.trim() || (currentUser?.roles.some(role => role.id === 'admin-role-id') && !createFormData.agencyId) ? 'not-allowed' : 'pointer'
                }}
              >
                <span className="material-symbols-outlined small">add</span>
                {isCreating ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;


